import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  CancelledError,
  DisallowedContentError,
  NoTargetLanguageError,
  refusalMessage,
  useDeleteStoryMutation,
  useGeneratedStoryListQuery,
  useScanMutation,
  useSetStoryMarkMutation,
  useStoryListQuery,
} from "./queries";
import { fillPlaceholders, lstr } from "./localization";
import { Link, useNavigate } from "react-router-dom";
import {
  PiArrowRight,
  PiCamera,
  PiCheckBold,
  PiClipboardText,
  PiSparkle,
} from "react-icons/pi";
import { StoryDescriptor } from "./story";
import { Modal } from "./Modal";
import { useLoggedIn, useUser } from "./firebase";
import { ProgressOverlay } from "./ProgressOverlay";
import { EmailVerificationNotice } from "./EmailVerificationNotice";
import { PracticeStatus, usePracticeStatusQuery } from "./practiceApi";
import {
  ContinueReadingCard,
  FeaturedStoryCard,
  StoryLibrary,
  StoryRow,
  StoryRows,
  isFinished,
  isListable,
  primaryTitle,
  storyOfTheDay,
} from "./StoryList";
import {
  isInProgress,
  loadReadingEntries,
  readingProgressByStoryId,
} from "./readingProgress";
import { ProgressRing } from "./ProgressRing";
import { SkeletonBlock, SkeletonStoryRows } from "./Skeleton";

function greeting(l: string, date: Date): string {
  const strings = lstr(l);
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return strings.greeting_morning;
  if (hour >= 12 && hour < 18) return strings.greeting_afternoon;
  if (hour >= 18 && hour < 23) return strings.greeting_evening;
  return strings.greeting_night;
}

function Greeting({ l }: { l: string }) {
  const user = useUser();
  const firstName = user?.displayName?.trim().split(/\s+/)[0];
  return (
    <header>
      <h1 className="font-literata text-[28px] font-bold leading-tight tracking-tight text-main-text sm:text-[32px]">
        {greeting(l, new Date())}
        {firstName && <>, {firstName}</>}
      </h1>
      <p className="mt-1 text-secondary-text">{lstr(l).home_subtitle}</p>
    </header>
  );
}

// practiceSummary is the one line telling what practice has in store now.
function practiceSummary(l: string, status: PracticeStatus) {
  const strings = lstr(l);
  if (status.levelSource === "default") {
    return { text: strings.practice_card_placement, done: false };
  }
  if (status.due > 0) {
    return {
      text: fillPlaceholders(strings.practice_home_due, { count: status.due }),
      done: false,
    };
  }
  const newLeft = status.dailyGoal - status.newToday;
  if (status.paused === "" && newLeft > 0) {
    return {
      text: fillPlaceholders(strings.practice_card_new, { count: newLeft }),
      done: false,
    };
  }
  return { text: strings.practice_card_done, done: true };
}

// PracticeTodayCard is the daily habit at a glance: a ring filling up with
// today's new words, and what's waiting in practice.
function PracticeTodayCard({ l, r }: { l: string; r: string }) {
  const strings = lstr(l);
  const status = usePracticeStatusQuery(r, true);

  if (status.isPending) {
    return (
      <div className="card flex items-center gap-4 p-4">
        <SkeletonBlock className="h-14 w-14 rounded-full" />
        <div className="flex flex-grow flex-col gap-2">
          <SkeletonBlock className="h-3 w-16" />
          <SkeletonBlock className="h-4 w-40" />
        </div>
      </div>
    );
  }

  const summary = status.data
    ? practiceSummary(l, status.data)
    : { text: strings.practice_home_start, done: false };
  const goalProgress = status.data
    ? status.data.newToday / Math.max(1, status.data.dailyGoal)
    : 0;

  return (
    <Link
      to="/practice"
      className="card-interactive group flex items-center gap-4 p-4"
    >
      <ProgressRing
        value={summary.done ? 1 : goalProgress}
        size={56}
        strokeWidth={5}
      >
        {summary.done ? (
          <PiCheckBold size={20} className="text-primary" />
        ) : (
          status.data && (
            <span className="text-[13px] font-bold tabular-nums text-main-text">
              {status.data.newToday}/{status.data.dailyGoal}
            </span>
          )
        )}
      </ProgressRing>
      <div className="min-w-0 flex-grow">
        <div className="eyebrow text-primary">{strings.practice_header}</div>
        <div className="mt-0.5 font-semibold leading-snug text-main-text">
          {summary.text}
        </div>
        {status.data && status.data.levelSource !== "default" && (
          <div className="mt-0.5 text-sm text-secondary-text">
            {fillPlaceholders(strings.practice_status_new, {
              done: status.data.newToday,
              goal: status.data.dailyGoal,
            })}
          </div>
        )}
      </div>
      <PiArrowRight
        size={18}
        className="shrink-0 text-secondary-text transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary"
      />
    </Link>
  );
}

function CreateTile({
  icon,
  label,
  emphasized = false,
  to,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  emphasized?: boolean;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const className = `flex flex-col items-center justify-center gap-2 rounded-2xl px-2 py-4 text-center text-sm font-semibold transition-[box-shadow,transform,background-color] duration-200 hover:-translate-y-px hover:shadow-lift active:translate-y-0 disabled:opacity-50 ${
    emphasized
      ? "bg-primary text-on-primary shadow-card hover:bg-primary-hover"
      : "card text-main-text"
  }`;
  const content = (
    <>
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-full ${
          emphasized ? "bg-white/15" : "bg-primary-light text-primary"
        }`}
      >
        {icon}
      </span>
      <span className="leading-tight">{label}</span>
    </>
  );
  return to ? (
    <Link to={to} className={className}>
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      {content}
    </button>
  );
}

// CreateTiles are the three ways to get a story of one's own. The scan tile
// owns the hidden file input that opens the platform's image picker (camera
// or gallery); the chosen images go to onScanFiles.
function CreateTiles({
  l,
  onScanFiles,
  scanDisabled,
}: {
  l: string;
  onScanFiles: (files: File[]) => void;
  scanDisabled: boolean;
}) {
  const strings = lstr(l);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    onScanFiles(Array.from(files));
    // Reset so picking the same files again still fires onChange.
    e.target.value = "";
  };

  return (
    <section className="mt-8">
      <h2 className="eyebrow mb-3">{strings.create_heading}</h2>
      <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
        <CreateTile
          to="/generate"
          emphasized
          icon={<PiSparkle size={20} />}
          label={strings.create_new_story}
        />
        <CreateTile
          onClick={() => inputRef.current?.click()}
          disabled={scanDisabled}
          icon={<PiCamera size={20} />}
          label={strings.create_scan}
        />
        <CreateTile
          to="/upload"
          icon={<PiClipboardText size={20} />}
          label={strings.create_paste}
        />
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={handleChange}
      />
    </section>
  );
}

// StoryMenu is the logged-in home: today's practice and reading at the top,
// ways to create a story, the user's own stories, and the library.
export function StoryMenu({ l, r }: { l: string; r: string }) {
  const navigate = useNavigate();
  const loggedIn = useLoggedIn();
  const strings = lstr(l);
  const query = useStoryListQuery(l, r);
  const queryGenerated = useGeneratedStoryListQuery(l, r);
  const deleteMutation = useDeleteStoryMutation();
  // Failures of scan, delete and story mark toggles, shown in a dialog.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const favoriteMutation = useSetStoryMarkMutation("favorite", () =>
    setErrorMessage(lstr(l).favorite_story_error),
  );
  const readMutation = useSetStoryMarkMutation("read", () =>
    setErrorMessage(lstr(l).read_story_error),
  );
  const scanMutation = useScanMutation();
  const scanAbortRef = useRef<AbortController | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Read once per visit of the page: progress only changes while reading.
  const readingEntries = useMemo(loadReadingEntries, []);
  const progressByStoryId = useMemo(
    () => readingProgressByStoryId(readingEntries),
    [readingEntries],
  );

  const confirmDelete = () => {
    if (!confirmDeleteId) return;
    // The dialog stays open, showing progress, until the server answers; a
    // failure closes it with an explanation instead of silently keeping the
    // story in the list.
    deleteMutation.mutate(confirmDeleteId, {
      onSuccess: () => setConfirmDeleteId(null),
      onError: () => {
        setConfirmDeleteId(null);
        setErrorMessage(lstr(l).delete_story_error);
      },
    });
  };

  const toggleFavorite = (storyId: string, favorite: boolean) => {
    favoriteMutation.mutate({ storyId, marked: favorite });
  };

  const toggleRead = (storyId: string, read: boolean) => {
    readMutation.mutate({ storyId, marked: read });
  };

  const handleScanFiles = (files: File[]) => {
    if (!loggedIn) {
      navigate("/login");
      return;
    }
    if (scanMutation.isPending) return;
    setErrorMessage(null);
    scanAbortRef.current = new AbortController();
    scanMutation.mutate({
      images: files,
      r,
      signal: scanAbortRef.current.signal,
    });
  };

  const cancelScan = () => {
    scanAbortRef.current?.abort();
  };

  // Pump scan results into navigation / error UI. Doing this in an effect
  // (instead of inside the button click handler) lets useScanMutation own its
  // pending/error state and survives React strict-mode double-invocations.
  useEffect(() => {
    if (scanMutation.isSuccess && scanMutation.data) {
      const id = scanMutation.data.id;
      scanMutation.reset();
      navigate(`/generated/${id}`);
    }
  }, [scanMutation.isSuccess, scanMutation.data, scanMutation, navigate]);

  useEffect(() => {
    if (scanMutation.isError) {
      // User-initiated cancellation isn't a real error - silently reset so
      // the next scan attempt can happen without a stray error toast.
      if (scanMutation.error instanceof CancelledError) {
        scanMutation.reset();
        return;
      }
      const scanErrorToMessage = (err: unknown): string => {
        if (err instanceof NoTargetLanguageError)
          return lstr(l).scan_no_target_text_error;
        if (err instanceof DisallowedContentError)
          return lstr(l).scan_error_disallowed;
        return refusalMessage(err, lstr(l)) ?? lstr(l).scan_error;
      };
      setErrorMessage(scanErrorToMessage(scanMutation.error));
      scanMutation.reset();
    }
  }, [scanMutation.isError, scanMutation.error, scanMutation, l]);

  const allStories: StoryDescriptor[] = [
    ...(queryGenerated.data ?? []),
    ...(query.data ?? []),
  ];
  // Stories marked read on the server are done, whatever this device saw.
  const continueEntry = readingEntries.find(
    (entry) =>
      entry.r === r &&
      isInProgress(entry.progress) &&
      !allStories.find((s) => s.id === entry.storyId)?.read,
  );
  const featured = query.data
    ? storyOfTheDay(query.data, r, progressByStoryId)
    : undefined;
  const featuredCard = featured && (
    <FeaturedStoryCard
      story={featured}
      l={l}
      r={r}
      eyebrow={strings.story_of_the_day}
      finished={isFinished(featured, progressByStoryId)}
    />
  );

  const storyPendingDeletion = queryGenerated.data?.find(
    (story) => story.id === confirmDeleteId,
  );
  const storyPendingDeletionTitle =
    storyPendingDeletion && primaryTitle(storyPendingDeletion, r);
  const generatedStories = (queryGenerated.data ?? []).filter(isListable);

  return (
    <div className="w-full pb-12">
      {confirmDeleteId && (
        <Modal
          showCloseButton={false}
          locale={l}
          closeModal={() => setConfirmDeleteId(null)}
        >
          <div className="flex flex-col items-center gap-4 py-2 text-center">
            <p className="font-literata text-xl font-bold text-main-text">
              {strings.delete_story_confirm_title}
            </p>
            {/* Naming the story is what lets the confirmation catch a tap
                on the wrong row. */}
            {storyPendingDeletionTitle && (
              <p
                lang={storyPendingDeletionTitle.locale}
                className="font-literata text-base font-medium italic text-main-text"
              >
                {storyPendingDeletionTitle.title}
              </p>
            )}
            <p className="text-sm text-secondary-text">
              {strings.delete_story_confirm_message}
            </p>
            <div className="mt-2 flex w-full gap-3">
              <button
                className="btn-secondary flex-1 py-2.5"
                onClick={() => setConfirmDeleteId(null)}
              >
                {strings.cancel_button}
              </button>
              <button
                disabled={deleteMutation.isPending}
                className="btn flex-1 bg-red-600 py-2.5 text-white hover:bg-red-700"
                onClick={confirmDelete}
              >
                {deleteMutation.isPending
                  ? strings.delete_story_deleting
                  : strings.delete_story_confirm_button}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {scanMutation.isPending && (
        <ProgressOverlay
          l={l}
          message={strings.scan_overlay_message}
          icon={<PiCamera />}
          onCancel={cancelScan}
        />
      )}

      {errorMessage && (
        <Modal
          showCloseButton={true}
          locale={l}
          closeModal={() => setErrorMessage(null)}
        >
          <p className="py-2 text-center text-base text-main-text">
            {errorMessage}
          </p>
        </Modal>
      )}

      <Greeting l={l} />

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <PracticeTodayCard l={l} r={r} />
        {continueEntry ? (
          <ContinueReadingCard l={l} entry={continueEntry} />
        ) : query.isPending ? (
          <SkeletonBlock className="h-[88px] rounded-2xl" />
        ) : (
          featuredCard
        )}
      </div>

      <CreateTiles
        l={l}
        onScanFiles={handleScanFiles}
        scanDisabled={scanMutation.isPending}
      />
      <EmailVerificationNotice l={l} className="mt-3" />

      {(queryGenerated.isError || generatedStories.length > 0) && (
        <section className="mt-10">
          <h2 className="mb-3 font-literata text-2xl font-bold text-main-text">
            {strings.my_stories_header}
          </h2>
          {queryGenerated.isError ? (
            <div className="text-sm text-error">
              {strings.loading_generated_story_list_error}
            </div>
          ) : (
            <StoryRows>
              {generatedStories.map((story) => (
                <StoryRow
                  key={story.id}
                  s={story}
                  l={l}
                  r={r}
                  progress={progressByStoryId.get(story.id)}
                  onDelete={setConfirmDeleteId}
                  onToggleFavorite={toggleFavorite}
                  onToggleRead={toggleRead}
                />
              ))}
            </StoryRows>
          )}
        </section>
      )}

      {continueEntry && featuredCard && (
        <section className="mt-10">{featuredCard}</section>
      )}

      <div className="mt-10">
        {query.isPending && (
          <>
            <SkeletonBlock className="mb-4 h-7 w-32" />
            <SkeletonStoryRows />
          </>
        )}
        {query.isError && (
          <div className="card px-6 py-8 text-center text-secondary-text">
            {strings.loading_story_list_error}
          </div>
        )}
        {query.isSuccess && (
          <StoryLibrary
            l={l}
            r={r}
            stories={query.data}
            progressByStoryId={progressByStoryId}
            onToggleFavorite={toggleFavorite}
            onToggleRead={toggleRead}
          />
        )}
      </div>
    </div>
  );
}
